/**
 * NewClientInvitationForm Component
 * Feature 028: SP Referral to New Clients (Not Yet in App)
 *
 * Reusable form for inviting new clients with optional provider or resource referral
 */

import { useState, forwardRef, useImperativeHandle } from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useInviteClient } from '../hooks/useInviteClient';

type ReferralType = 'PROVIDER' | 'RESOURCE';

export type NewClientInvitationFormProps = {
  referralType: ReferralType;
  referralId: string;
  onSuccess: () => void;
  onCancel: () => void;
};

type FormData = {
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  personalMessage: string;
};

export type NewClientInvitationFormHandle = {
  submit: () => void;
  isPending: boolean;
};

export const NewClientInvitationForm = forwardRef<NewClientInvitationFormHandle, NewClientInvitationFormProps>(
  ({ referralType, referralId, onSuccess }, ref) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState<FormData>({
      clientFirstName: '',
      clientLastName: '',
      clientEmail: '',
      personalMessage: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState<string | null>(null);

    const { mutate: inviteClient, isPending } = useInviteClient(user?.id || '', {
      onSuccess: () => {
        onSuccess();
      },
      onError: (error) => {
        const errorMessage = error?.message || 'Unable to send invitation. Please try again.';

        if (errorMessage.includes('already registered')) {
          setApiError('This email is already registered. Please use "Share with Existing Client" instead.');
        } else if (errorMessage.includes('does not exist')) {
          setApiError(`The ${referralType.toLowerCase()} you are trying to share no longer exists.`);
        } else {
          setApiError(errorMessage);
        }
      },
    });

    const validateForm = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!formData.clientFirstName.trim()) {
        newErrors.clientFirstName = 'First name is required';
      }

      if (!formData.clientLastName.trim()) {
        newErrors.clientLastName = 'Last name is required';
      }

      if (!formData.clientEmail.trim()) {
        newErrors.clientEmail = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
        newErrors.clientEmail = 'Please enter a valid email address';
      }

      if (formData.personalMessage && formData.personalMessage.length > 500) {
        newErrors.personalMessage = 'Personal message must be 500 characters or less';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
      if (!validateForm()) {
        return;
      }

      setApiError(null);

      const payload = {
        clientFirstName: formData.clientFirstName.trim(),
        clientLastName: formData.clientLastName.trim(),
        clientEmail: formData.clientEmail.trim(),
        personalMessage: formData.personalMessage.trim() || undefined,
        referredProviderId: referralType === 'PROVIDER' ? referralId : undefined,
        referredResourceId: referralType === 'RESOURCE' ? referralId : undefined,
      };

      inviteClient(payload);
    };

    // Expose submit method to parent via ref
    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      isPending,
    }));

    const characterCount = formData.personalMessage.length;
    const characterLimit = 500;

    return (
      <div className="space-y-4">
        {/* Error Message */}
        {apiError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{apiError}</AlertDescription>
          </Alert>
        )}

        {/* Info Message */}
        <Alert>
          <AlertDescription className="text-sm">
            This will create a new account for the client and automatically share{' '}
            {referralType === 'PROVIDER' ? 'the provider' : 'the resource'} with them.
          </AlertDescription>
        </Alert>

        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            First Name *
          </label>
          <Input
            id="firstName"
            type="text"
            value={formData.clientFirstName}
            onChange={(e) =>
              setFormData({ ...formData, clientFirstName: e.target.value })
            }
            disabled={isPending}
            aria-invalid={!!errors.clientFirstName}
            aria-describedby={errors.clientFirstName ? 'firstName-error' : undefined}
          />
          {errors.clientFirstName && (
            <p id="firstName-error" className="mt-1 text-sm text-red-600">
              {errors.clientFirstName}
            </p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name *
          </label>
          <Input
            id="lastName"
            type="text"
            value={formData.clientLastName}
            onChange={(e) =>
              setFormData({ ...formData, clientLastName: e.target.value })
            }
            disabled={isPending}
            aria-invalid={!!errors.clientLastName}
            aria-describedby={errors.clientLastName ? 'lastName-error' : undefined}
          />
          {errors.clientLastName && (
            <p id="lastName-error" className="mt-1 text-sm text-red-600">
              {errors.clientLastName}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <Input
            id="email"
            type="email"
            value={formData.clientEmail}
            onChange={(e) =>
              setFormData({ ...formData, clientEmail: e.target.value })
            }
            disabled={isPending}
            aria-invalid={!!errors.clientEmail}
            aria-describedby={errors.clientEmail ? 'email-error' : undefined}
          />
          {errors.clientEmail && (
            <p id="email-error" className="mt-1 text-sm text-red-600">
              {errors.clientEmail}
            </p>
          )}
        </div>

        {/* Personal Message */}
        <div>
          <label htmlFor="personalMessage" className="block text-sm font-medium text-gray-700 mb-1">
            Personal Message (Optional)
          </label>
          <textarea
            id="personalMessage"
            rows={4}
            value={formData.personalMessage}
            onChange={(e) =>
              setFormData({ ...formData, personalMessage: e.target.value })
            }
            placeholder={`Add a note about why you're recommending this ${referralType.toLowerCase()}...`}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isPending}
            aria-invalid={!!errors.personalMessage}
            aria-describedby="personalMessage-count"
          />
          <div className="mt-1 flex items-center justify-between">
            {errors.personalMessage ? (
              <p className="text-sm text-red-600">{errors.personalMessage}</p>
            ) : (
              <p id="personalMessage-count" className="text-xs text-gray-500">
                {characterCount}/{characterLimit} characters
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

NewClientInvitationForm.displayName = 'NewClientInvitationForm';
